<template>
    <UTooltip :text="tooltip">
        <UButton
            color="neutral"
            variant="ghost"
            size="sm"
            :icon="icon"
            :aria-label="tooltip"
            @click="toggle"
        />
    </UTooltip>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { useAppStore } from '@/stores/app'

    /**
     * The house colour-mode switch (vue-spec `theme.md`): TWO states, Light and Dark — there is
     * no System option anywhere in the UI.
     *
     * The icon shows the CURRENT mode (sun in light, moon in dark) and the tooltip names the mode
     * a click switches TO, so the button says what it will do.
     *
     * `appStore.colorMode` is VueUse's `useColorMode` WITHOUT `emitAuto`, so it always reads the
     * RESOLVED mode — 'light' or 'dark' — even when the stored preference is still the first-visit
     * default ('auto', i.e. whatever the OS prefers) or was written as 'auto' by @nuxt/ui's own
     * `useDark()`. Writing 'light' / 'dark' persists an explicit choice under the same
     * `vueuse-color-scheme` key the pre-paint script in `index.html` reads. Nothing here touches
     * the `dark` class by hand.
     */

    type ColorMode = 'light' | 'dark'

    const ICONS: Record<ColorMode, string> = {
        light: 'i-lucide-sun',
        dark:  'i-lucide-moon',
    }

    const { t }    = useI18n()
    const appStore = useAppStore()

    const current = computed((): ColorMode => appStore.colorMode === 'dark' ? 'dark' : 'light')

    const next = computed((): ColorMode => current.value === 'dark' ? 'light' : 'dark')

    const icon = computed((): string => ICONS[current.value])

    const tooltip = computed((): string => t(`color_mode.switch_to_${next.value}`))

    const toggle = (): void => {
        appStore.colorMode = next.value
    }
</script>
